import React from 'react';
import moment from 'moment';
import { sparkDateValidation } from '../../../helpers/validationHelper';
import './more-details.scss';

interface NamedItem {
  name: string;
}

interface SuiteMoreDetails {
  number?: string;
  stratalot?: string;
  buildingName?: string;
  floor?: string | number;
  status?: string;
  bedrooms?: string | number;
  bathrooms?: string | number;
  kind?: string;
  floorplan_id?: string | number;
  floorplan_name?: string;
  area?: string | number;
  parkingStalls?: NamedItem[];
  storageLockers?: NamedItem[];
  color_schemes?: string | null;
  estimated_closing_date?: string;
  estimated_completion?: string;
  estimated_move_in?: string;
  updated_at?: string;
}

interface MoreDetailsProps {
  details: SuiteMoreDetails;
  closePopup: () => void;
}

// legacy/workaround: Spark sends color_schemes as a JSON-encoded object map
const extractColorSchemeName = (
  colorSchemesJson: string | null | undefined
): string | undefined => {
  if (!colorSchemesJson) {
    return undefined;
  }

  const schemes = JSON.parse(colorSchemesJson);
  if (!schemes) {
    return undefined;
  }

  const [firstScheme] = Object.values(schemes) as Array<NamedItem | undefined>;
  if (!firstScheme || !firstScheme.name) {
    return undefined;
  }

  return firstScheme.name;
};

const MoreDetails = ({ closePopup, details }: MoreDetailsProps) => {
  const colorScheme = extractColorSchemeName(details.color_schemes);

  return (
    <div className="suite-more-details">
      <div className="panel">
        <div className="heading">
          <h4>Information</h4>
        </div>
        <div onClick={closePopup} className="close-button">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="30"
            height="30"
            fill="currentColor"
            viewBox="0 0 16 16"
          >
            <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z" />
          </svg>
        </div>
        <div className="content">
          <div className="field">
            <p className="field-label">Unit</p>
            <p>{details.number}</p>
          </div>
          {details.stratalot && (
            <div className="field">
              <p className="field-label">Strata lot</p>
              <p>{details.stratalot}</p>
            </div>
          )}
          {details.buildingName && (
            <div className="field">
              <p className="field-label">Building</p>
              <p>{details.buildingName}</p>
            </div>
          )}
          {details.floor && (
            <div className="field">
              <p className="field-label">Floor</p>
              <p>{details.floor}</p>
            </div>
          )}
          <div className="field">
            <p className="field-label">Status</p>
            <p>{details.status}</p>
          </div>
          <div className="field">
            <p className="field-label">Bedrooms</p>
            <p>{details.bedrooms}</p>
          </div>
          <div className="field">
            <p className="field-label">Bathrooms</p>
            <p>{details.bathrooms}</p>
          </div>
          <div className="field">
            <p className="field-label">Kind</p>
            <p>{details.kind}</p>
          </div>
          <div className="field">
            <p className="field-label">Floorplan id</p>
            <p>{details.floorplan_id}</p>
          </div>
          <div className="field">
            <p className="field-label">Floorplan name</p>
            <p>{details.floorplan_name}</p>
          </div>
          <div className="field">
            <p className="field-label">Area (square footage)</p>
            <p>{details.area}</p>
          </div>
          {details.parkingStalls && details.parkingStalls.length > 0 && (
            <div className="field">
              <p className="field-label">Parking stall numbers</p>
              {details.parkingStalls.map((stall: NamedItem, index: number) => (
                <p key={index}>
                  {stall.name +
                    (details.parkingStalls!.length - 1 === index ? '' : ', ')}
                </p>
              ))}
            </div>
          )}
          {details.storageLockers && details.storageLockers.length > 0 && (
            <div className="field">
              <p className="field-label">Storage locker numbers</p>
              {details.storageLockers.map((locker: NamedItem, index: number) => (
                <p key={index}>
                  {locker.name +
                    (details.storageLockers!.length - 1 === index ? '' : ', ')}
                </p>
              ))}
            </div>
          )}
          {colorScheme && (
            <div className="field">
              <p className="field-label">Color scheme</p>
              <p>{colorScheme}</p>
            </div>
          )}
          {sparkDateValidation(details.estimated_closing_date) && (
            <div className="field">
              <p className="field-label">Estimated closing date</p>
              <p>
                {moment(details.estimated_closing_date).format(
                  'MM/DD/YYYY HH:mm:ss'
                )}
              </p>
            </div>
          )}
          {sparkDateValidation(details.estimated_completion) && (
            <div className="field">
              <p className="field-label">Estimated completion</p>
              <p>
                {moment(details.estimated_completion).format(
                  'MM/DD/YYYY HH:mm:ss'
                )}
              </p>
            </div>
          )}
          {sparkDateValidation(details.estimated_move_in) && (
            <div className="field">
              <p className="field-label">Estimated move in</p>
              <p>
                {moment(details.estimated_move_in).format(
                  'MM/DD/YYYY HH:mm:ss'
                )}
              </p>
            </div>
          )}
          {sparkDateValidation(details.updated_at) && (
            <div className="field">
              <p className="field-label">Updated at</p>
              <p>{moment(details.updated_at).format('MM/DD/YYYY HH:mm:ss')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MoreDetails;